import { useHistory } from 'react-router';
import { useForm, Controller } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { InputAdornment, MenuItem } from '@material-ui/core';
import { Autocomplete, TextField, Backdrop, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import Api from 'api';
import Joi from 'joi';
import date from '@joi/date';
import { TopBarRedesigned } from 'components/TopBarRedesigned';
import { FormMaterialCalendarPicker } from 'components/Inputs/MaterialCalendarPicker';
import { StyledButton } from 'components/Buttons/DefaultButton/DefaultButton.styles';
import { PageContainer, MainContent, Subtitle, TimeRow, Note, FieldLabel, StyledSelect } from './ManualBookShiftPage.styles';
import { FormMaterialSelector } from 'components/Selector';
import { useApi, useBlurFocusedInputOnUnmount } from 'hooks';
import { useMemo, useEffect, useState, useCallback } from 'react';
import { SimpleModal } from 'components/Modal';
import ModalTitleContainer from 'components/Modal/components/ModalTitleContainer';
import { exitReact } from 'utils';
import { formatDateTo12HourTimeString, formatDateTo24HourTimeString } from 'utils/time';
import { TIME_GENERATION } from 'utils/constants';
import { navigateManualBookingHome } from './manualBookingNavigation';

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

const generateTimeOptions = (
    from: number = TIME_GENERATION.DEFAULT_START_HOUR, 
    to: number = TIME_GENERATION.DEFAULT_END_HOUR, 
    step: number = TIME_GENERATION.DEFAULT_STEP_MINUTES
) => {
    const times = [];
    for (let hour = from; hour <= to; hour++) {
        for (let minute = 0; minute < 60; minute += step) {
            const time = new Date(); time.setHours(hour, minute);
            times.push({
                value: formatDateTo24HourTimeString(time),
                label: formatDateTo12HourTimeString(time),
            });
        }
    }
    return times;
};
const timeOptions = generateTimeOptions();

const joi = Joi.extend(date);
const manualBookShiftSchema = (constraints: { minRate: number, maxRate: number }, officeLabels: string[] = []) => joi
  .object({
    officeName: (
      officeLabels.length > 0
        ? joi
            .string()
            .valid(...officeLabels)
            .messages({ 'any.only': 'Please select an office from the list' })
        : joi.string()
    )
      .label('Office Name')
      .required(),
    shiftDate: joi
      .any()
      .custom((value: any, helper: any) =>
        value && dayjs(value).isValid() ? value : helper.message('Invalid date')
      )
      .label('Shift Date')
      .required(),
    arrivalTime: joi
      .string()
      .pattern(/^\d{2}:\d{2}$/)
      .label('Arrival Time')
      .required(),
    endTime: joi
      .string()
      .pattern(/^\d{2}:\d{2}$/)
      .label('End Time')
      .required(),
    hourlyRate: joi
      .number()
      .label('Hourly Rate')
      .positive()
      .min(constraints.minRate)
      .max(constraints.maxRate)
      .required(),
  })
  .custom((obj: any, helper: any) => {
    if (obj.arrivalTime && obj.endTime) {
      const [ah, am] = obj.arrivalTime.split(':').map(Number);
      const [eh, em] = obj.endTime.split(':').map(Number);
      const arrival = ah * 60 + am;
      const end = eh * 60 + em;
      if (end <= arrival) {
        return helper.message({ custom: 'End Time must be after Arrival Time' });
      }
    }
    return obj;
  });

interface ManualBookShiftFormValues {
  officeName: string;
  shiftDate: Dayjs | null;
  arrivalTime: string;
  endTime: string;
  hourlyRate: number | '';
}

type ManualBookShiftErrorType =
  | { type: 'INVALID_LOCATION' }
  | { type: 'INVALID_HYGIENIST' }
  | { type: 'INVALID_DATE' }
  | { type: 'INVALID_SHIFT_TIME' }
  | { type: 'UNEXPECTED_ERROR' }
  | { type: 'DATE_ALREADY_BOOKED' }
  | { type: 'INVALID_DENTIST' }
  | { type: 'DENTIST_ALREADY_BOOKED' }
  | { type: 'INTERNAL_SERVER_ERROR' }
  | { type: 'BEFORE_REGISTER_DATE'; registrationUnixTimestamp: number }
;

export default function ManualBookShiftPage() {
  useBlurFocusedInputOnUnmount();
  const [fetchedData, loading, error, loadData] = useApi({
    apiMethod: Api.getManualBookShiftPageData,
    initialValue: null,
  })
  useEffect(() => {
    loadData({});
  }, []);

  const { offices, hourlyRates, constraints, hygienist } = fetchedData || {};
  const noLocationsModalOpen = !loading && (!offices || offices?.length === 0);
  const userTimeZone = hygienist?.timeZone || null;

  const history = useHistory();
  const handleBack = () => {
    history.push({ pathname: 'hygienistHome' });
  };

  const handleNoLocationsClose = () => {
    history.push({ pathname: 'hygienistHome' });
  };

  const officeLabels = useMemo(() => (
    offices?.map((office: any) => `${office.practiceName} - ${office.address}, ${office.city}`) || []
  ), [offices]);

  const formSchema = useMemo(() => {
    return manualBookShiftSchema(constraints || { minRate: 0, maxRate: 0 }, officeLabels);
  }, [constraints, officeLabels]);

  const {
    control,
    handleSubmit,
    formState: { isValid },
    setValue,
    setError,
    clearErrors,
    reset,
    watch,
  } = useForm<ManualBookShiftFormValues>({
    mode: 'onChange',
    resolver: joiResolver(formSchema),
    defaultValues: {
      officeName: undefined,
      shiftDate: undefined,
      arrivalTime: undefined,
      endTime: undefined,
      hourlyRate: undefined,
    },
  });

  const errorMessages: Record<ManualBookShiftErrorType['type'], string> = {
    INVALID_LOCATION: 'Selected office is not valid.',
    INVALID_HYGIENIST: 'Your account is not eligible to book this shift.',
    INVALID_DATE: 'Please choose a valid shift date.',
    INVALID_SHIFT_TIME: 'End time must be after arrival time and within the selected day.',
    DATE_ALREADY_BOOKED: 'Opps, it looks like you\'re already booked for that date! Please try again.',
    INVALID_DENTIST: 'Unable to verify dentist information for this office.',
    DENTIST_ALREADY_BOOKED: 'This dentist already has the maximum number of shifts for that day.',
    UNEXPECTED_ERROR: 'Something went wrong while booking. Please try again.',
    BEFORE_REGISTER_DATE: 'Sorry, there\'s been an error. Please contact support.',
    INTERNAL_SERVER_ERROR: 'Something went wrong while booking. Please try again.',
  };

  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [mustSelectNewDate, setMustSelectNewDate] = useState(false);
  const watchedShiftDate = watch('shiftDate');
  const [minStartUnix, setMinStartUnix] = useState<number | null>(null);
  const watchedArrivalTime = watch('arrivalTime');

  const convertToUtcUnix = useCallback((dateStr: string, timeStr?: string | null) => {
    if (!dateStr || !timeStr) {
      return null;
    }
    const localDateStr = `${dateStr} ${timeStr}`;
    if (userTimeZone) {
      return dayjs.tz(localDateStr, 'YYYY-MM-DD HH:mm', userTimeZone).utc().unix();
    }
    return dayjs(localDateStr).utc().unix();
  }, [userTimeZone]);

  const isStartAfterMin = useMemo(() => {
    if (!minStartUnix || !watchedShiftDate || !watchedArrivalTime) return false;
    const shiftDateStr = dayjs(watchedShiftDate).format('YYYY-MM-DD');
    const startUnix = convertToUtcUnix(shiftDateStr, watchedArrivalTime);
    if (startUnix === null) return false;
    return startUnix > minStartUnix;
  }, [minStartUnix, watchedShiftDate, watchedArrivalTime, convertToUtcUnix]);

  useEffect(() => {
    if (!minStartUnix || !watchedShiftDate) return;
    const minStartStr = dayjs.unix(minStartUnix).local().format('YYYY-MM-DD h:mm A');
    const minStartLocal = dayjs.unix(minStartUnix).local();
    const selectedDate = dayjs(watchedShiftDate);

    let invalid = false;

    // If the selected date is before the minimum allowed date, it's invalid regardless of time
    if (selectedDate.isBefore(minStartLocal, 'day')) {
      invalid = true;
    } else if (selectedDate.isSame(minStartLocal, 'day')) {
      // Same day as the minimum: need to check arrival time if present
      if (watchedArrivalTime) {
        const shiftDateStr = selectedDate.format('YYYY-MM-DD');
        const startUnixForValidation = convertToUtcUnix(shiftDateStr, watchedArrivalTime);
        invalid = startUnixForValidation !== null && startUnixForValidation <= minStartUnix;
      } else {
        // Without arrival time, do not mark invalid yet (user may choose a later time)
        invalid = false;
      }
    } else {
      // Selected date is after the minimum date
      invalid = false;
    }

    if (invalid) {
      setError('shiftDate', { type: 'manual', message: `Start time must be after ${minStartStr}` });
      setError('arrivalTime', { type: 'manual', message: `Start time must be after ${minStartStr}` });
    } else {
      clearErrors('shiftDate');
      clearErrors('arrivalTime');
    }
  }, [minStartUnix, watchedShiftDate, watchedArrivalTime, setError, clearErrors, convertToUtcUnix]);

  const onSubmit = async (data: ManualBookShiftFormValues) => {
    const selectedOffice = offices?.find(office =>
      office.practiceName === data.officeName ||
      `${office.practiceName} - ${office.address}, ${office.city}` === data.officeName
    );

    if (!selectedOffice) {
      alert('Unable to determine the selected office. Please choose a valid office from the list.');
      return;
    }

    setSubmitting(true);
    try {
      const shiftDateStr = dayjs(data.shiftDate).format('YYYY-MM-DD');
      const startUnixUtc = convertToUtcUnix(shiftDateStr, data.arrivalTime);
      const endUnixUtc = convertToUtcUnix(shiftDateStr, data.endTime);

      if (startUnixUtc === null || endUnixUtc === null) {
        setErrorModalMessage('Unable to determine the shift times. Please try again.');
        setErrorModalOpen(true);
        return;
      }

      // Enforce dynamic requirement when backend returned BEFORE_REGISTER_DATE previously
      if (minStartUnix) {
        if (startUnixUtc <= minStartUnix) {
          const minStartStr = dayjs.unix(minStartUnix).local().format('YYYY-MM-DD h:mm A');
          setError('shiftDate', { type: 'manual', message: `Start time must be after ${minStartStr}` });
          setError('arrivalTime', { type: 'manual', message: `Start time must be after ${minStartStr}` });
          setErrorModalMessage(`Your selected start time is too early.\nPlease choose a start time after ${minStartStr}.`);
          setErrorModalOpen(true);
          return;
        }
      }

      const payload = {
        locationId: selectedOffice.id,
        shiftDate: shiftDateStr,
        startTime: startUnixUtc,
        endTime: endUnixUtc,
        hourlyRate: Number(data.hourlyRate),
      };

      await Api.manuallyBookShift(payload)

      setSuccessModalOpen(true);

    } catch (err: any) {
      const parseManualBookShiftError = (err: any): ManualBookShiftErrorType | null => {
        let parsedMessage = null;
        try {
          parsedMessage = JSON.parse(err.message);
        } catch (e) {
          return null;
        }

        if (!parsedMessage) {
          return null;
        }

        const error = parsedMessage.error;
        if (!error) {
          return null;
        }

        return error;
      };

      const manualBookShiftError = parseManualBookShiftError(err);
      if (manualBookShiftError) {
        setErrorModalMessage(errorMessages[manualBookShiftError.type] || 'Unable to book shift. Please try again later.');
        if (manualBookShiftError.type === 'BEFORE_REGISTER_DATE') {
          const rawTs = manualBookShiftError.registrationUnixTimestamp;
          const tsSeconds = rawTs > 1000000000000 ? Math.floor(rawTs / 1000) : rawTs; // normalize to seconds
          setMinStartUnix(tsSeconds);
          setMustSelectNewDate(true);
          reset();
        }
        setErrorModalOpen(true);
        return;
      }

      setErrorModalMessage('Unable to book shift. Please try again later.');
      setErrorModalOpen(true);

    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <TopBarRedesigned
        onBack={handleBack}
        title="Manually Book a Shift"
      />
      <MainContent>
      <Subtitle>
        Use this to enter details for a shift arranged directly with a TempStars office.
      </Subtitle>

      <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
        <div className="list-block inset inputBox">
          <FieldLabel htmlFor="officeName">Office Name</FieldLabel>
          <Controller
            name="officeName"
            control={control}
            render={({ field, fieldState }) => (
              <Autocomplete
                freeSolo
                options={offices?.map(office => ({
                  label: `${office.practiceName} - ${office.address}, ${office.city}`,
                  value: office.practiceName,
                  id: office.id,
                  lastHourlyRate: office.lastHourlyRate
                })) || []}
                disablePortal
                ListboxProps={{ className: 'no-fastclick' }}
                componentsProps={{
                  popper: { className: 'no-fastclick' },
                  paper: { className: 'no-fastclick' },
                  listbox: { className: 'no-fastclick' }
                } as any}
                getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                inputValue={field.value || ''}
                onChange={(event, newValue: any) => {
                  if (!newValue) {
                    field.onChange('');
                    setError('officeName', { type: 'manual', message: 'Please select an office from the list' });
                    return;
                  }

                  if (typeof newValue === 'string') {
                    field.onChange(newValue);
                    const selected = offices?.find(office => `${office.practiceName} - ${office.address}, ${office.city}` === newValue);
                    if (selected?.lastHourlyRate && constraints) {
                      setValue('hourlyRate', Math.min(selected.lastHourlyRate, constraints.maxRate));
                    }
                    if (officeLabels.includes(newValue)) {
                      clearErrors('officeName');
                    }
                    return;
                  }

                  field.onChange(newValue.label);
                  if (newValue?.lastHourlyRate && constraints) {
                    setValue('hourlyRate', Math.min(newValue.lastHourlyRate, constraints.maxRate));
                  }
                  clearErrors('officeName');
                }}
                onInputChange={(event, newInputValue) => {
                  field.onChange(newInputValue);

                  const selectedOption = offices?.find(office => 
                    `${office.practiceName} - ${office.address}, ${office.city}` === newInputValue
                  );
                  if (selectedOption?.lastHourlyRate && constraints) {
                    setValue('hourlyRate', Math.min(selectedOption.lastHourlyRate, constraints.maxRate));
                  }

                  if (officeLabels.includes(newInputValue)) {
                    clearErrors('officeName');
                  } else {
                    setError('officeName', { type: 'manual', message: 'Please select an office from the list' });
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    id="officeName"
                    variant="outlined"
                    className="styledInput"
                    fullWidth
                    placeholder="Search for office name"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start" style={{ paddingLeft: 8, paddingRight: 8 }}>
                          <SearchIcon fontSize="small" style={{ color: '#6d6d72' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            )}
          />
        </div>

        <div className="list-block inset inputBox">
          <FieldLabel htmlFor="shiftDate">Shift Date</FieldLabel>
          <div style={{ width: '100%' }}>
            <FormMaterialCalendarPicker
              control={control}
              name="shiftDate"
              label=""
              format="YYYY-MM-DD"
              className="styledInput"
              startAdornmentIcon={<CalendarTodayIcon fontSize="small" style={{ color: '#6d6d72' }} />}
            />
          </div>
        </div>

        <TimeRow className="list-block inset inputBox">
          <div style={{ flex: 1 }}>
            <FieldLabel htmlFor="arrivalTime">Arrival Time</FieldLabel>
            <Controller
              name="arrivalTime"
              control={control}
              render={({ field }) => (
                <StyledSelect
                  className="no-fastclick"
                  {...field}
                  value={field.value ?? ''}
                  variant="outlined"
                  fullWidth
                  displayEmpty
                  renderValue={(selected: any) => (
                    <div style={{ display: 'flex', alignItems: 'center', color: selected ? undefined : '#6d6d72' }}>
                      <AccessTimeIcon fontSize="small" style={{ color: '#6d6d72', marginRight: 8 }} />
                      {selected ? (timeOptions.find(o => o.value === selected)?.label || selected) : 'Select arrival time'}
                    </div>
                  )}
                >
                  {timeOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </StyledSelect>
              )}
            />
          </div>
          <div style={{ flex: 1 }}>
            <FieldLabel htmlFor="endTime">End Time</FieldLabel>
            <Controller
              name="endTime"
              control={control}
              render={({ field }) => (
                <StyledSelect
                  className="no-fastclick"
                  {...field}
                  value={field.value ?? ''}
                  variant="outlined"
                  fullWidth
                  displayEmpty
                  renderValue={(selected: any) => (
                    <div style={{ display: 'flex', alignItems: 'center', color: selected ? undefined : '#6d6d72' }}>
                      <AccessTimeIcon fontSize="small" style={{ color: '#6d6d72', marginRight: 8 }} />
                      {selected ? (timeOptions.find(o => o.value === selected)?.label || selected) : 'Select end time'}
                    </div>
                  )}
                >
                  {timeOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </StyledSelect>
              )}
            />
          </div>
        </TimeRow>

        <div className="list-block inset inputBox">
          <FieldLabel htmlFor="hourlyRate">Hourly Rate</FieldLabel>
          <FormMaterialSelector
            control={control}
            name="hourlyRate"
            label=""
            className="styledInput"
            startAdornmentIcon={<AttachMoneyIcon fontSize="small" style={{ color: '#6d6d72' }} />}
            formatSelectedValue={(selected) => `$${selected}/hr`}
            placeholder="Select your rate"
          >
            {hourlyRates?.map((rate: number) => (
              <MenuItem key={rate} value={rate}>{`$${rate}/hr`}</MenuItem>
            )) || []}
          </FormMaterialSelector>
        </div>

        <StyledButton
          type="submit"
          variant="contained"
          color="primary"
          disabled={!isValid || submitting || (mustSelectNewDate && minStartUnix !== null && !isStartAfterMin)}
          className="no-fastclick"
        >
          Put this shift in my schedule
        </StyledButton>
      </form>

      <div style={{ maxWidth: '100%', marginTop: 32 }}>
        <div
          style={{
            maxWidth: '100%',
            background: '#f0f6fe',
            border: '1px solid #b6daff',
            borderRadius: 12,
            padding: '16px',
          }}
        >
          <Note style={{ color: '#445ab6' }}>
            <strong>Note</strong>: This shift will be added to your schedule once confirmed. You'll receive a confirmation email with all the details.
          </Note>
        </div>
      </div>

      </MainContent>

      {/* Success Modal */}
      <SimpleModal isOpen={successModalOpen} onClose={() => {
        setSuccessModalOpen(false);
        navigateManualBookingHome(history);
      }}>
        <ModalTitleContainer title="Shift Successfully Booked" onClose={() => {
          setSuccessModalOpen(false);
          navigateManualBookingHome(history);
        }} />
        <div style={{ padding: '1rem', textAlign: 'center' }}>
          Your shift has been added to your schedule. You will receive a confirmation email shortly.
        </div>
        <StyledButton
          variant="contained"
          color="primary"
          onClick={() => {
            setSuccessModalOpen(false);
            navigateManualBookingHome(history);
          }}
          style={{ alignSelf: 'center', marginTop: '1rem' }}
        >
          OK
        </StyledButton>
      </SimpleModal>

      {/* Error Modal */}
      <SimpleModal isOpen={errorModalOpen} onClose={() => setErrorModalOpen(false)}>
        <ModalTitleContainer title="Booking Failed" onClose={() => setErrorModalOpen(false)} />
        <div style={{ padding: '1rem', textAlign: 'center', whiteSpace: 'pre-line' }}>
          {errorModalMessage}
        </div>
        <StyledButton
          variant="contained"
          color="primary"
          onClick={() => setErrorModalOpen(false)}
          style={{ alignSelf: 'center', marginTop: '1rem' }}
        >
          OK
        </StyledButton>
      </SimpleModal>

      {/* Global submitting backdrop */}
      <Backdrop
        open={submitting}
        style={{ zIndex: 2000, color: '#fff' }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Modal shown when no locations are available */}
      <SimpleModal isOpen={noLocationsModalOpen} onClose={handleNoLocationsClose}>
        <ModalTitleContainer title="No Previous Locations" onClose={handleNoLocationsClose} />
        <div style={{ padding: '1rem', textAlign: 'center' }}>
          You can only manually book shifts at locations where you have previously worked.
        </div>
        <StyledButton
          variant="contained"
          color="primary"
          onClick={handleNoLocationsClose}
          style={{ alignSelf: 'center', marginTop: '1rem' }}
        >
          OK
        </StyledButton>
      </SimpleModal>
    </PageContainer>
  );
}
